import { describeError } from '../lib/api/client'
import type { SearchHit } from '../lib/api/generated'
import { searchApiV1SearchGet } from '../lib/api/generated'
import type { ChatItem } from '../lib/types'

/**
 * Home's fallback when Riley cannot answer: run the palette search on the words that matter and show the matching
 * cases inline. "where is the yyzmt room phone case" searches `yyzmt`, `room` and `phone` one at a time (the search
 * API matches one substring), and a case that matches more of them ranks first. A failed search says so (P11): it is
 * never shown as "nothing matched".
 */

const FILLER = new Set(
  `a an and any are as at be by can case cases do does find for from get give go got has have how i id in is it its
  me my of on open or our please show so that the their them there these this those to up us was we what whats when
  where wheres which who why will with you your ticket tickets about look looking riley tell need`
    .split(/\s+/)
    .filter(Boolean),
)
const MAX_TERMS = 4
const MAX_HITS = 8

/** The search terms in a sentence: case numbers (C-00012) and ticket numbers (#23136) first, then content words. */
export function searchTerms(text: string): string[] {
  const out: string[] = []
  const push = (t: string) => {
    if (t && !out.includes(t)) out.push(t)
  }
  for (const m of text.matchAll(/\bc-?\d{1,6}\b/gi)) {
    const digits = m[0].replace(/\D/g, '')
    push(`C-${digits.padStart(5, '0')}`)
  }
  for (const m of text.matchAll(/#(\d{3,8})\b/g)) push(m[1] ?? '')
  const words = text
    .toLowerCase()
    .replace(/[’']s\b/g, '')
    .split(/[^a-z0-9.@-]+/)
    .map((w) => w.replace(/^[.-]+|[.-]+$/g, ''))
    .filter((w) => w.length >= 2 && !FILLER.has(w) && !/^c-?\d+$/.test(w))
  for (const w of words) push(w)
  return out.slice(0, MAX_TERMS)
}

type Outcome = { ok: true; hits: SearchHit[] } | { ok: false; error: string; hits: SearchHit[] }

async function searchOne(q: string): Promise<Outcome> {
  try {
    const r = await searchApiV1SearchGet({ query: { q } })
    if (r.error === undefined && Array.isArray(r.data)) return { ok: true, hits: r.data }
    // 502 conduit_unreachable still carries the Patchbay-side matches (cases and tickets are local).
    const body = (r.error ?? {}) as { local_results?: SearchHit[]; detail?: string }
    if (Array.isArray(body.local_results)) return { ok: true, hits: body.local_results }
    return { ok: false, error: body.detail || `search answered ${r.response?.status ?? 'nothing'}`, hits: [] }
  } catch (err) {
    return { ok: false, error: describeError(err), hits: [] }
  }
}

/** Case and ticket hits for `text`, best first, with how many search terms each matched. */
export async function searchCasesFor(text: string): Promise<{ terms: string[]; hits: SearchHit[]; failed: string | null }> {
  const terms = searchTerms(text)
  if (!terms.length) return { terms, hits: [], failed: null }
  const results = await Promise.all(terms.map(searchOne))
  const score = new Map<string, { hit: SearchHit; n: number; order: number }>()
  let order = 0
  for (const r of results) {
    for (const hit of r.hits) {
      if (hit.kind !== 'case' && hit.kind !== 'ticket') continue
      const key = `${hit.kind}:${hit.id}`
      const seen = score.get(key)
      if (seen) seen.n += 1
      else score.set(key, { hit, n: 1, order: order++ })
    }
  }
  // A ticket that already sits in a listed case (same href) adds nothing.
  const ranked = [...score.values()].sort(
    (a, b) => b.n - a.n || (a.hit.kind === b.hit.kind ? 0 : a.hit.kind === 'case' ? -1 : 1) || a.order - b.order,
  )
  const hrefs = new Set<string>()
  const hits: SearchHit[] = []
  for (const { hit } of ranked) {
    const key = hit.href ?? `${hit.kind}:${hit.id}`
    if (hrefs.has(key)) continue
    hrefs.add(key)
    hits.push(hit)
    if (hits.length >= MAX_HITS) break
  }
  const failures = results.filter((r): r is Extract<Outcome, { ok: false }> => !r.ok)
  return { terms, hits, failed: failures.length === results.length ? (failures[0]?.error ?? 'search failed') : null }
}

/** The Riley-side line Home shows when Riley could not answer: matching cases as links, or what failed. */
export async function fallbackSearchItem(text: string, id: string): Promise<ChatItem | null> {
  const { terms, hits, failed } = await searchCasesFor(text)
  if (!terms.length) return null
  const quoted = terms.join(', ')
  const base = { id: `search-${id}`, role: 'riley' as const, at: 'Just now' }
  if (hits.length) {
    return {
      ...base,
      text: `While Riley is unavailable, here is what search found for ${quoted}:`,
      links: hits,
    }
  }
  if (failed) return { ...base, text: `Search is unreachable too (${failed}); no cases could be looked up.` }
  return { ...base, text: `Search found no case or ticket matching ${quoted}.` }
}
